/**
 * DRPX Courier — Admin-Funktionen (Supabase)
 *
 * Enthält alle privilegierten Operationen: User-Auth-Änderungen (via Edge
 * Function), Fahrer-Zertifikate, Audit-Log, Plattform-Settings, Analytics-
 * RPCs, Re-Engagement-Admin und den Admin-Big-Sprint (Migration 057).
 *
 * @module lib/supabase/admin
 */

import { _sb } from './client.js';
import {
  mapFahrerZertifikatFromDb,
  mapAuditLogFromDb,
  mapProfilTextvorlageFromDb,
} from './mappers.js';
import { validateUploadFileDeep, sanitizeFileName } from '../upload.js';
import { todayLocal, MS_PER_DAY } from '../format.js';
import type { ApiError } from '../api.js';
import type { Rolle, PlattformSettings, FahrerZertStatus, FahrerZertCacheEntry } from '../../types';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

/**
 * Einheitliche Fehler-Instanz, wenn der Supabase-Client nicht initialisiert
 * ist (z.B. wenn die .env-Variablen fehlen). Wird in den Null-Guards am
 * Anfang jeder Funktion verwendet, damit Caller konsistente Fehler-Shapes
 * bekommen statt harter Crashs.
 */
const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

/**
 * Extrahiert eine Fehler-Nachricht aus einem `unknown`-Wert (typisch aus
 * `catch (e: unknown)`).
 */
function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * Validiert einen `unknown`-Wert gegen den FahrerZertStatus-Union.
 * Gibt `undefined` zurück, wenn der Wert kein gültiger Status ist.
 */
function parseFahrerZertStatus(v: unknown): FahrerZertStatus | undefined {
  if (v === 'pruefung' || v === 'gueltig' || v === 'abgelehnt' || v === 'abgelaufen') {
    return v;
  }
  return undefined;
}

// ════════════════════════════════════════════════════════════════════
// Lokale Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

interface AdminActionResult {
  success?: boolean;
  error?: string;
}

interface ZertifikatMeta {
  dateiName?: string;
  dateiGroesse?: number;
  hochgeladenAm?: string;
  gueltigBis?: string;
  status?: 'pruefung' | 'gueltig' | 'abgelaufen' | 'abgelehnt';
  filePath?: string;
}

type ZertifikatBatchMap = Record<string, Record<string, FahrerZertCacheEntry>>;

interface PendingZert {
  fahrerId: string;
  zertKey: string;
}

interface ExpiredZert {
  fahrerId: string;
  zertKey: string;
  gueltigBis: string;
}

interface SignedUrlResult {
  url?: string;
  error?: string;
}

interface ProfilTextvorlageInput {
  name?: string;
  text?: string;
}

export interface ReengagementStats {
  mails30d: number;
  mails7d: number;
  mails24h: number;
  wiederbelebt30d: number;
  wiederbelebt7d: number;
  inactiveTotal: number;
  conversionRate: number;
}

export interface AutopilotStatus {
  enabled: boolean;
  schedule: unknown;
  lastRun: unknown;
  nextRunInfo: unknown;
}

interface ToggleAutopilotResult extends AdminActionResult {
  enabled?: boolean;
}

export interface UsageStatsTotals {
  total24h: number;
  total7d: number;
  total30d: number;
  byType: Record<string, number>;
}

// ════════════════════════════════════════════════════════════════════
// Shared Unions + Row-Type Guards
// ════════════════════════════════════════════════════════════════════

/** Empfehlungs-Keys für `RouteTrendRow.empfehlung` (Migration 055). */
export type RouteEmpfehlung = 'unterbesetzt' | 'preise_steigend' | 'preise_fallend' | 'stabil';

const ROUTE_EMPFEHLUNG_KEYS: readonly RouteEmpfehlung[] = [
  'unterbesetzt',
  'preise_steigend',
  'preise_fallend',
  'stabil',
];

/** Validiert einen unbekannten RPC-Wert gegen die `RouteEmpfehlung`-Union. */
function asRouteEmpfehlung(v: unknown): RouteEmpfehlung {
  const s = String(v ?? '');
  return (ROUTE_EMPFEHLUNG_KEYS as readonly string[]).includes(s)
    ? (s as RouteEmpfehlung)
    : 'stabil';
}

const ROLLE_KEYS: readonly Rolle[] = ['firma', 'fahrer', 'admin', 'mitarbeiter'];

/** Validiert einen unbekannten RPC-Wert gegen die `Rolle`-Union. */
function asRolle(v: unknown): Rolle {
  const s = String(v ?? '');
  return (ROLLE_KEYS as readonly string[]).includes(s) ? (s as Rolle) : 'fahrer';
}

/** Required string: konvertiert `unknown` zu non-empty string (leer = ''). */
function asStr(v: unknown): string {
  return v == null ? '' : String(v);
}

/** Nullable string: `null` bei `null`/`undefined`, sonst `String(v)`. */
function asStrOrNull(v: unknown): string | null {
  return v == null ? null : String(v);
}

/** Required number: konvertiert zu number, invalid → 0. */
function asNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Nullable number: `null` bei `null`/`undefined`, sonst `Number(v)` (invalid → 0). */
function asNumOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** `value`-Feld von `PlatformHealthRow` — RPC liefert `string | number`. */
function asStrOrNum(v: unknown): string | number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return v;
  return String(v ?? '');
}

// ════════════════════════════════════════════════════════════════════
// Row-Types (RPC-Responses)
// ════════════════════════════════════════════════════════════════════

export interface PlatformHealthRow {
  metric: string;
  value: string | number;
  status: string;
  detail: string | null;
}

export interface ConversionFunnelRow {
  stage: string;
  userCount: number;
  prozent: number;
}

export interface ComplaintUserRow {
  userId: string;
  firma: string | null;
  email: string;
  rolle: Rolle;
  schlechteAnzahl: number;
  durchschnitt: number;
  /** ISO-Timestamp der letzten schlechten Bewertung. */
  letzteSchlechte: string;
}

export interface ActivityTrendRow {
  userId: string;
  email: string;
  firma: string | null;
  rolle: Rolle;
  eventsLast30: number;
  eventsPrev30: number;
  /** `null` wenn keine Previous-Daten (z.B. neuer Account). */
  rueckgangProzent: number | null;
}

export interface RouteTrendRow {
  abholCity: string;
  zielCity: string;
  fahrzeug: string | null;
  auftraege30: number;
  auftraegePrev30: number;
  avgPreis30: number | null;
  avgPreisPrev30: number | null;
  avgAngebotePrAuftrag: number | null;
  trendProzent: number | null;
  empfehlung: RouteEmpfehlung;
}

/** Ein Gerät (Supabase-Sitzung) eines Kontos — Migration 131. */
export interface SitzungRow {
  sitzungId: string;
  /** ISO-Timestamp der Anmeldung. */
  angemeldetAm: string;
  /** ISO-Timestamp der letzten Aktivität (Genauigkeit ca. 1 Stunde). */
  zuletztAktiv: string;
  /** Zuletzt aktiv vor weniger als 12 h und von Supabase nicht beendet. */
  istAktiv: boolean;
  ipAdresse: string | null;
  /** Voller User-Agent-Text, Anzeige über `geraetKurz()`. */
  userAgent: string | null;
}

/** Je Konto: Zahl der aktiven Geräte — Migration 131. */
export interface SitzungenUebersichtRow {
  kontoId: string;
  firma: string | null;
  vorname: string | null;
  nachname: string | null;
  rolle: Rolle;
  nutzerNr: string | null;
  aktiveGeraete: number;
  geraete30Tage: number;
  /** ISO-Timestamp oder `null`. */
  zuletztAktiv: string | null;
}

/** Status eines Mitarbeiter-Zugangs in `firma_mitglieder` — Migration 132. */
export type MitgliedStatus = 'eingeladen' | 'aktiv' | 'gesperrt';

/** Zuordnung Mitarbeiter-Zugang → Firmenkonto (DRPX-Pro) — Migration 132. */
export interface FirmaMitgliedRow {
  /** Profil-ID des Mitarbeiter-Zugangs (Rolle `mitarbeiter`). */
  userId: string;
  /** Profil-ID des Firmenkontos (Rolle `firma`). */
  firmaId: string;
  status: MitgliedStatus;
}

export interface InactiveUserRow {
  userId: string;
  email: string;
  firma: string | null;
  vorname: string | null;
  rolle: Rolle;
  /** ISO-Timestamp oder `null` wenn nie aktiv. */
  letzteAktivitaet: string | null;
  tageInaktiv: number;
  bereitsGesendet: boolean;
  optOut: boolean;
}

// Row-Typen für Supabase-Responses, die wir nur strukturell nutzen
interface ZertifikatMapRow {
  fahrerId?: unknown;
  zertKey?: unknown;
  status?: unknown;
  gueltigBis?: unknown;
}

// ════════════════════════════════════════════════════════════════════
// ADMIN: USER AUTH ÄNDERUNGEN (Edge Function)
// ════════════════════════════════════════════════════════════════════

/**
 * Setzt das Passwort eines beliebigen Users zurück — nur für Admins.
 * Ruft die Edge Function `admin-user-update` auf, die intern den
 * Service-Role-Key verwendet, um die Änderung in `auth.users` durchzuführen.
 */
export async function adminResetPassword(
  userId: string,
  newPassword: string,
): Promise<AdminActionResult> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const res = await client.functions.invoke('admin-user-update', {
      body: JSON.stringify({ userId: userId, password: newPassword }),
    });
    if (res.error) return { error: res.error.message || String(res.error) };
    const rawData: unknown = res.data;
    const data: unknown = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
    if (
      data &&
      typeof data === 'object' &&
      'error' in data &&
      (data as { error: unknown }).error
    ) {
      return { error: String((data as { error: unknown }).error) };
    }
    return { success: true };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}

/**
 * Ändert die E-Mail-Adresse eines beliebigen Users — nur für Admins.
 * Geht über die `admin-user-update` Edge Function (Service-Role).
 */
export async function adminChangeEmail(
  userId: string,
  newEmail: string,
): Promise<AdminActionResult> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const res = await client.functions.invoke('admin-user-update', {
      body: JSON.stringify({ userId: userId, email: newEmail }),
    });
    if (res.error) return { error: res.error.message || String(res.error) };
    const rawData: unknown = res.data;
    const data: unknown = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
    if (
      data &&
      typeof data === 'object' &&
      'error' in data &&
      (data as { error: unknown }).error
    ) {
      return { error: String((data as { error: unknown }).error) };
    }
    return { success: true };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}

// ════════════════════════════════════════════════════════════════════
// FAHRER ZERTIFIKATE
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt alle Zertifikate eines Fahrers (Führerschein, ADR, Gefahrgut, etc.)
 * für die Anzeige im Profil oder Admin-Panel.
 */
export async function getFahrerZertifikate(
  fahrerId: string,
): Promise<{ data: Array<Record<string, unknown>>; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_zertifikate').select('*').eq('fahrer_id', fahrerId);
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapFahrerZertifikatFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt Zertifikate für mehrere Fahrer in EINEM Query (statt N+1).
 * Liefert eine Map: `{ fahrerId: { zertKey: { status, gueltigBis } } }`.
 */
export async function getFahrerZertifikateBatch(
  fahrerIds: string[],
): Promise<{ data: ZertifikatBatchMap; error?: unknown }> {
  if (!_sb) return { data: {}, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    if (!fahrerIds || fahrerIds.length === 0) return { data: {} };
    const r = await client.from('fahrer_zertifikate').select('*').in('fahrer_id', fahrerIds);
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const result: ZertifikatBatchMap = {};
    rows.forEach((raw) => {
      const z = mapFahrerZertifikatFromDb(raw) as ZertifikatMapRow | null;
      if (!z) return;
      const fahrerId = typeof z.fahrerId === 'string' ? z.fahrerId : '';
      const zertKey = typeof z.zertKey === 'string' ? z.zertKey : '';
      if (!fahrerId || !zertKey) return;
      if (!result[fahrerId]) result[fahrerId] = {};
      result[fahrerId][zertKey] = {
        status: parseFahrerZertStatus(z.status),
        gueltigBis: typeof z.gueltigBis === 'string' ? z.gueltigBis : undefined,
      };
    });
    return { data: result };
  } catch (e: unknown) {
    return { data: {}, error: e };
  }
}

/**
 * Legt ein Zertifikat an oder aktualisiert ein bestehendes (Upsert über
 * den zusammengesetzten Key `fahrer_id + zert_key`).
 */
export async function upsertFahrerZertifikat(
  fahrerId: string,
  zertKey: string,
  meta: ZertifikatMeta,
): Promise<{ data: Record<string, unknown> | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      fahrer_id: fahrerId,
      zert_key: zertKey,
      datei_name: meta.dateiName || '',
      datei_groesse: meta.dateiGroesse || 0,
      hochgeladen_am: meta.hochgeladenAm || todayLocal(),
      gueltig_bis: meta.gueltigBis || '',
      status: meta.status || 'pruefung',
      file_path: meta.filePath || '',
    };
    const r = await client
      .from('fahrer_zertifikate')
      .upsert(d, { onConflict: 'fahrer_id,zert_key' })
      .select()
      .single();
    return {
      data: r.data ? mapFahrerZertifikatFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Ändert nur den Status eines Zertifikats (z.B. Admin prüft und setzt auf
 * "gueltig").
 */
export async function updateFahrerZertifikatStatus(
  fahrerId: string,
  zertKey: string,
  status: string,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_zertifikate')
      .update({ status: status })
      .eq('fahrer_id', fahrerId)
      .eq('zert_key', zertKey);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Admin korrigiert das Ablaufdatum eines Zertifikats (Audit 089 #4 UX-Folge,
 * 18.05.2026). Wenn das neue Datum in der Zukunft liegt UND der Status
 * 'abgelaufen' war, wird automatisch auf 'pruefung' reaktiviert (analog
 * zum Auto-Übergang aus Migration 052).
 *
 * Läuft als Admin durch `is_admin() → return NEW` im Trigger ohne
 * Probleme — Status-Manipulation hier ist explizit gewollt.
 */
export async function updateFahrerZertifikatGueltigBis(
  fahrerId: string,
  zertKey: string,
  gueltigBisIso: string,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  if (!gueltigBisIso) return { error: 'gueltigBisIso required' };
  const client = _sb;
  try {
    // Zuerst aktuellen Status lesen — falls 'abgelaufen' und neues Datum
    // in Zukunft, soll automatisch auf 'pruefung' zurückgesetzt werden.
    const cur = await client
      .from('fahrer_zertifikate')
      .select('status')
      .eq('fahrer_id', fahrerId)
      .eq('zert_key', zertKey)
      .maybeSingle();
    if (cur.error) return { error: cur.error };

    const currentStatus = (cur.data as { status?: string } | null)?.status || '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const newDate = new Date(gueltigBisIso + 'T00:00:00');
    const isInFuture = newDate > today;

    // Update-Payload zusammenbauen
    const update: Record<string, string> = { gueltig_bis: gueltigBisIso };
    if (currentStatus === 'abgelaufen' && isInFuture) {
      update.status = 'pruefung';
    }

    const r = await client
      .from('fahrer_zertifikate')
      .update(update)
      .eq('fahrer_id', fahrerId)
      .eq('zert_key', zertKey);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Lädt die eigentliche Zertifikats-Datei in den `zertifikate`-Bucket hoch.
 * Validiert Größe und MIME-Typ, sanitiert den Dateinamen. Upsert ist aktiv,
 * damit ein erneuter Upload die alte Datei ersetzt.
 *
 * **Return-Shape (Audit 088 Follow-Up, 18.05.2026):** Returnt jetzt
 * `{ data: { path }, error }` — kompatibel mit dem `api()`-Helper.
 * Vorher: `{ path, error }` (Root-Level), wodurch `api()` immer `null`
 * lieferte und der Caller in den Frühzeitig-Return fiel → Zertifikat-
 * Upload schlug stillschweigend fehl. Storage-Upload klappte zwar, aber
 * `upsertFahrerZertifikat` wurde nie aufgerufen.
 *
 * **Hinweis:** Diese Funktion räumt KEINE alte Datei auf — jeder Upload
 * generiert einen neuen Timestamp-Pfad. Der Caller muss nach erfolgreichem
 * `upsertFahrerZertifikat` die alte Datei via `deleteZertifikatFile`
 * entfernen (DSGVO + Storage-Kosten). Siehe `FahrerZertifikate.handleUpload`.
 */
export async function uploadZertifikatDatei(
  fahrerId: string,
  zertKey: string,
  file: File,
): Promise<{ data?: { path: string }; error?: ApiError | string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const valErr = await validateUploadFileDeep('zertifikate', file);
    if (valErr) return { error: valErr };
    const safeName = sanitizeFileName(file.name);
    const path = fahrerId + '/' + zertKey + '_' + Date.now() + '_' + safeName;
    const r = await client.storage.from('zertifikate').upload(path, file, { upsert: true });
    if (r.error) return { error: r.error.message };
    return { data: { path: r.data.path } };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}

/**
 * Löscht eine einzelne Datei aus dem `zertifikate`-Bucket.
 *
 * **Zweck (KYC-Audit 089 #4):** Beim Re-Upload eines Zertifikats wird eine
 * neue Datei mit neuem Timestamp-Pfad angelegt. Ohne expliziten Cleanup
 * blieben alte Versionen als Waisen im Bucket liegen → DSGVO-Akkumulation
 * (Daten länger gespeichert als nötig) + Storage-Kosten.
 *
 * Caller ruft das nach erfolgreichem `upsertFahrerZertifikat` auf, damit
 * der alte Pfad sicher gelöscht werden kann (Metadaten zeigen schon auf
 * den neuen Pfad).
 *
 * **Rollback-Anwendung:** Wenn der Caller einen frischen Upload wieder
 * verwerfen muss (z. B. weil das anschließende DB-Update fehlschlägt),
 * kann die neu hochgeladene Datei mit derselben Funktion entfernt werden.
 *
 * Fehler werden als `{ error }` zurückgegeben statt geworfen, damit Caller
 * sie best-effort behandeln können (z. B. ignorieren, wenn die Datei
 * längst weg ist).
 */
export async function deleteZertifikatFile(
  path: string,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  if (!path) return { error: null };
  const client = _sb;
  try {
    const r = await client.storage.from('zertifikate').remove([path]);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Erzeugt eine zeitlich begrenzte Signed URL (5 Minuten) für eine
 * Zertifikatsdatei.
 */
export async function getZertifikatUrl(path: string): Promise<SignedUrlResult> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.storage.from('zertifikate').createSignedUrl(path, 300);
    if (r.error) return { error: r.error.message };
    return { url: r.data.signedUrl };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}

/**
 * Gibt alle Zertifikate zurück, die auf Admin-Prüfung warten.
 * Wird im Admin-KYC-Panel verwendet, um die Prüfungs-Queue zu bauen.
 */
export async function getAllPendingZerts(): Promise<{
  data: PendingZert[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_zertifikate')
      .select('fahrer_id, zert_key, status')
      .eq('status', 'pruefung');
    const rows = (r.data || []) as Array<{ fahrer_id: string; zert_key: string }>;
    return {
      data: rows.map((z) => ({ fahrerId: z.fahrer_id, zertKey: z.zert_key })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Löscht ein Zertifikat inklusive Datei aus dem Storage-Bucket.
 * Storage-Fehler werden ignoriert (z.B. wenn die Datei bereits weg ist).
 */
export async function deleteFahrerZertifikat(
  fahrerId: string,
  zertKey: string,
  filePath?: string,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    if (filePath) {
      await client.storage.from('zertifikate').remove([filePath]);
    }
    const r = await client
      .from('fahrer_zertifikate')
      .delete()
      .eq('fahrer_id', fahrerId)
      .eq('zert_key', zertKey);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// PLATTFORM SETTINGS
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt die globalen Plattform-Einstellungen (eine einzige Zeile mit
 * `id='global'` und JSONB-Payload). Beispiele: Wartungsmodus, Banner-Text,
 * Feature-Flags.
 */
export async function getPlattformSettings(): Promise<{
  data: Record<string, unknown>;
  error?: unknown;
}> {
  if (!_sb) return { data: {}, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('plattform_settings')
      .select('settings')
      .eq('id', 'global')
      .single();
    // Fehler der Datenbank weitergeben (18.10.2026) — vorher kam bei einem
    // Fehler `{}` ohne `error` zurück und überschrieb gespeicherte Werte.
    if (r.error) return { data: {}, error: r.error };
    const settings =
      r.data && typeof r.data === 'object' && 'settings' in r.data
        ? ((r.data as { settings: unknown }).settings as Record<string, unknown>)
        : {};
    return { data: settings || {} };
  } catch (e: unknown) {
    return { data: {}, error: e };
  }
}

/**
 * Speichert die globalen Plattform-Einstellungen (Upsert auf `id='global'`).
 *
 * Rückgabe-Shape: `{ data: true | null, error }` — data ist `true` bei Erfolg,
 * damit der `api()`-Wrapper den Erfolg vom Fehler unterscheiden kann. Ein
 * reines `{ error }` ohne data würde im api()-Wrapper fälschlich als null
 * interpretiert und "success" wäre von "error" nicht zu unterscheiden.
 */
export async function savePlattformSettings(
  settings: PlattformSettings,
): Promise<{ data: true | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('plattform_settings')
      .upsert({ id: 'global', settings: settings, updated_at: new Date().toISOString() })
      .eq('id', 'global');
    return { data: r.error ? null : true, error: r.error };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// AUDIT LOG
// ════════════════════════════════════════════════════════════════════

/**
 * Schreibt einen Eintrag ins Audit-Log für Nachvollziehbarkeit.
 * Wird bei sicherheitsrelevanten Aktionen aufgerufen (Login, Account-
 * Löschung, etc.).
 *
 * **B20 Stufe 3 (Migration 104):** Läuft über den SECURITY-DEFINER-RPC
 * `write_audit_log` statt eines direkten Table-Inserts. Die `user_id`
 * setzt der Server aus `auth.uid()` (nicht fälschbar); die strenge
 * INSERT-Policy aus Migration 052 bleibt unverändert. Direkte Inserts
 * durch Nicht-Admins scheiterten seit 052 mit 42501 — deshalb fehlten
 * sämtliche Nutzer-Aktions-Logs im Admin-Audit-Log.
 *
 * `details` kann ein Objekt (für strukturiertes Logging) oder ein String
 * (für Freitext, z.B. aus der Admin-UI) sein. Strings werden beim
 * Speichern als `{ message: "..." }` gewrappt, damit die Spalte
 * einheitlich jsonb bleibt (Migration 058).
 *
 * Voraussetzung: eingeloggte Session (der RPC verlangt `auth.uid()`).
 */
export async function writeAuditLog(
  aktion: string,
  details?: string | Record<string, unknown> | null,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    // Details normalisieren → immer jsonb-kompatibel (Objekt oder null)
    let detailsJson: Record<string, unknown> | null = null;
    if (details != null) {
      if (typeof details === 'string') {
        detailsJson = details === '' ? null : { message: details };
      } else {
        detailsJson = details;
      }
    }
    const r = await client.rpc('write_audit_log', {
      p_aktion: aktion || '',
      p_details: detailsJson,
    });
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Lädt die letzten N Audit-Log-Einträge für die Admin-Ansicht.
 */
export async function getAuditLog(
  limit?: number,
): Promise<{ data: Array<Record<string, unknown>>; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit || 500);
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapAuditLogFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Leert das komplette Audit-Log. VORSICHT: nur für Admins, unwiderruflich.
 * Normalerweise sollte das Log nicht gelöscht werden — diese Funktion ist
 * für Not-Aufräum-Aktionen gedacht (z.B. Testdaten entfernen).
 */
export async function clearAuditLog(): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('audit_log')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000');
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// PROFIL TEXTVORLAGEN
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt alle Profil-Textvorlagen eines Users (vorgefertigte Bausteine für
 * Leistungsbeschreibungen, AGB-Auszüge, etc.).
 */
export async function getProfilTextvorlagen(
  userId: string,
): Promise<{ data: Array<Record<string, unknown>>; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('profil_textvorlagen')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapProfilTextvorlageFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Legt eine neue Profil-Textvorlage an.
 */
export async function createProfilTextvorlage(
  userId: string,
  v: ProfilTextvorlageInput,
): Promise<{ data: Record<string, unknown> | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = { user_id: userId, name: v.name || '', text: v.text || '' };
    const r = await client.from('profil_textvorlagen').insert(d).select().single();
    return {
      data: r.data ? mapProfilTextvorlageFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Löscht eine Profil-Textvorlage unwiderruflich.
 */
export async function deleteProfilTextvorlage(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('profil_textvorlagen').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// EXPIRY CHECKS
// ════════════════════════════════════════════════════════════════════

/**
 * Gibt alle Fahrer-Zertifikate mit abgelaufenem Datum zurück.
 */
export async function getExpiredZerts(): Promise<{
  data: ExpiredZert[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_zertifikate')
      .select('fahrer_id, zert_key, gueltig_bis, status')
      .lt('gueltig_bis', todayLocal());
    const rows = (r.data || []) as Array<{
      fahrer_id: string;
      zert_key: string;
      gueltig_bis: string;
    }>;
    return {
      data: rows.map((z) => ({
        fahrerId: z.fahrer_id,
        zertKey: z.zert_key,
        gueltigBis: z.gueltig_bis,
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Gibt alle Fahrer mit abgelaufenen VERSICHERUNGEN zurück.
 * Filtert speziell auf `versicherung_transport` und `versicherung_erweitert`.
 * Compliance-Workflow: Admin kann diese User mit einem Klick pausieren.
 */
export async function getExpiredVersicherungen(): Promise<{
  data: ExpiredZert[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_zertifikate')
      .select('fahrer_id, zert_key, gueltig_bis, status')
      .in('zert_key', ['versicherung_transport', 'versicherung_erweitert'])
      .lt('gueltig_bis', todayLocal());
    const rows = (r.data || []) as Array<{
      fahrer_id: string;
      zert_key: string;
      gueltig_bis: string;
    }>;
    return {
      data: rows.map((z) => ({
        fahrerId: z.fahrer_id,
        zertKey: z.zert_key,
        gueltigBis: z.gueltig_bis,
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// ACTIVITY / ANALYTICS
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt die User-Activity-Trend View — vergleicht Events der letzten
 * 30 Tage mit den 30 Tagen davor. Liefert Rückgang in Prozent.
 * NUR FÜR ADMINS (RLS prüft `is_admin()`).
 */
export async function getActivityTrend(): Promise<{
  data: ActivityTrendRow[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('user_activity_trend')
      .select('*')
      .order('rueckgang_prozent', { ascending: false, nullsFirst: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((u) => ({
        userId: asStr(u.user_id),
        email: asStr(u.email),
        firma: asStrOrNull(u.firma),
        rolle: asRolle(u.rolle),
        eventsLast30: asNum(u.events_last_30),
        eventsPrev30: asNum(u.events_prev_30),
        rueckgangProzent: asNumOrNull(u.rueckgang_prozent),
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt Top-Routen mit Preis-Trends und Empfehlungen.
 * Datenquelle: `get_route_trends` RPC (Migration 055).
 * RPC prüft selbst Marktplatz-Zugang oder Admin.
 */
export async function getRouteTrends(
  limit?: number,
): Promise<{ data: RouteTrendRow[]; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_route_trends', { p_limit: limit || 10 });
    if (r.error) return { data: [] };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((row) => ({
        abholCity: asStr(row.abhol_city),
        zielCity: asStr(row.ziel_city),
        fahrzeug: asStrOrNull(row.fahrzeug),
        auftraege30: asNum(row.auftraege_30),
        auftraegePrev30: asNum(row.auftraege_prev_30),
        avgPreis30: asNumOrNull(row.avg_preis_30),
        avgPreisPrev30: asNumOrNull(row.avg_preis_prev_30),
        avgAngebotePrAuftrag: asNumOrNull(row.avg_angebote_pro_auftrag),
        trendProzent: asNumOrNull(row.trend_prozent),
        empfehlung: asRouteEmpfehlung(row.empfehlung),
      })),
    };
  } catch {
    return { data: [] };
  }
}

// ════════════════════════════════════════════════════════════════════
// RE-ENGAGEMENT ADMIN (Migration 056)
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt die Liste der heute betroffenen inaktiven Abonnenten.
 */
export async function getInactiveUsersToday(): Promise<{
  data: InactiveUserRow[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_inactive_users_today');
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((u) => ({
        userId: asStr(u.user_id),
        email: asStr(u.email),
        firma: asStrOrNull(u.firma),
        vorname: asStrOrNull(u.vorname),
        rolle: asRolle(u.rolle),
        letzteAktivitaet: asStrOrNull(u.letzte_aktivitaet),
        tageInaktiv: asNum(u.tage_inaktiv),
        bereitsGesendet: Boolean(u.bereits_gesendet),
        optOut: Boolean(u.opt_out),
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt die Re-Engagement-Statistik (Mails + Wiederbelebt-Counts).
 */
export async function getReengagementStats(): Promise<{
  data: ReengagementStats | null;
  error?: unknown;
}> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_reengagement_stats');
    if (r.error) return { data: null, error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const row = rows[0] || ({} as Record<string, unknown>);
    const n = (v: unknown): number => (typeof v === 'number' ? v : 0);
    return {
      data: {
        mails30d: n(row.mails_30d),
        mails7d: n(row.mails_7d),
        mails24h: n(row.mails_24h),
        wiederbelebt30d: n(row.wiederbelebt_30d),
        wiederbelebt7d: n(row.wiederbelebt_7d),
        inactiveTotal: n(row.inactive_total),
        conversionRate: n(row.conversion_rate),
      },
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Lädt den aktuellen Status des Autopilot-Cron-Jobs.
 */
export async function getReengagementAutopilotStatus(): Promise<{
  data: AutopilotStatus | null;
  error?: unknown;
}> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_reengagement_autopilot_status');
    if (r.error) return { data: null, error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const row = rows[0] || ({} as Record<string, unknown>);
    return {
      data: {
        enabled: !!row.enabled,
        schedule: row.schedule,
        lastRun: row.last_run,
        nextRunInfo: row.next_run_info,
      },
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Schaltet den Autopilot-Cron-Job an oder aus.
 */
export async function toggleReengagementAutopilot(
  enable: boolean,
): Promise<ToggleAutopilotResult> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('toggle_reengagement_autopilot', { p_enable: !!enable });
    if (r.error) return { error: r.error.message };
    const data = r.data as { error?: unknown; enabled?: unknown } | null;
    if (data && data.error) return { error: String(data.error) };
    return { success: true, enabled: !!(data && data.enabled) };
  } catch (e: unknown) {
    return { error: errMsg(e) };
  }
}

// ════════════════════════════════════════════════════════════════════
// USAGE STATS
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt aggregierte Usage-Statistiken (Gesamt-Counts pro Event-Typ und
 * Zeitraum).
 */
export async function getUsageStatsTotals(): Promise<{
  data: UsageStatsTotals | null;
  error?: unknown;
}> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    // Events 24h, 7d, 30d
    const now = Date.now();
    const h24 = new Date(now - MS_PER_DAY).toISOString();
    const d7 = new Date(now - 7 * MS_PER_DAY).toISOString();
    const d30 = new Date(now - 30 * MS_PER_DAY).toISOString();

    const [r24, r7, r30] = await Promise.all([
      client
        .from('usage_events')
        .select('event_type', { count: 'exact', head: true })
        .gte('created_at', h24),
      client
        .from('usage_events')
        .select('event_type', { count: 'exact', head: true })
        .gte('created_at', d7),
      client
        .from('usage_events')
        .select('event_type', { count: 'exact', head: true })
        .gte('created_at', d30),
    ]);

    // Pro Typ in den letzten 30 Tagen
    const r = await client.from('usage_events').select('event_type').gte('created_at', d30);
    const rows = (r.data || []) as Array<{ event_type: string }>;

    const byType: Record<string, number> = {};
    rows.forEach((e) => {
      byType[e.event_type] = (byType[e.event_type] || 0) + 1;
    });

    return {
      data: {
        total24h: r24.count || 0,
        total7d: r7.count || 0,
        total30d: r30.count || 0,
        byType: byType,
      },
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// ADMIN BIG-SPRINT (Migration 057)
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt Plattform-Health-Check Übersicht.
 */
export async function getPlatformHealth(): Promise<{
  data: PlatformHealthRow[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_platform_health');
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((row) => ({
        metric: asStr(row.metric),
        value: asStrOrNum(row.value),
        status: asStr(row.status),
        detail: asStrOrNull(row.detail),
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt Conversion-Funnel (anonyme Aggregat-Counts).
 */
export async function getConversionFunnel(
  days?: number,
): Promise<{ data: ConversionFunnelRow[]; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_conversion_funnel', { p_days: days || 30 });
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((row) => ({
        stage: asStr(row.stage),
        userCount: asNum(row.user_count),
        prozent: asNum(row.prozent),
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt User mit zu vielen schlechten Bewertungen.
 */
export async function getComplaintUsers(
  minComplaints?: number,
): Promise<{ data: ComplaintUserRow[]; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_complaint_users', {
      p_min_complaints: minComplaints || 3,
    });
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((row) => ({
        userId: asStr(row.user_id),
        firma: asStrOrNull(row.firma),
        email: asStr(row.email),
        rolle: asRolle(row.rolle),
        schlechteAnzahl: asNum(row.schlechte_anzahl),
        durchschnitt: asNum(row.durchschnitt),
        letzteSchlechte: asStr(row.letzte_schlechte),
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// GERÄTE / SITZUNGEN (Migration 131, 15.10.2026)
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt die Geräte (Supabase-Sitzungen) eines Kontos aus den letzten
 * 30 Tagen, aktive zuerst. Nur für Admins (Prüfung in der Datenbank).
 *
 * @param userId - Konto, dessen Geräte geladen werden
 */
export async function getAdminSitzungen(
  userId: string,
): Promise<{ data: SitzungRow[]; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('admin_get_sitzungen', { p_user_id: userId });
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((row) => ({
        sitzungId: asStr(row.sitzung_id),
        angemeldetAm: asStr(row.angemeldet_am),
        zuletztAktiv: asStr(row.zuletzt_aktiv),
        istAktiv: row.ist_aktiv === true,
        ipAdresse: asStrOrNull(row.ip_adresse),
        userAgent: asStrOrNull(row.geraet),
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt je Konto die Zahl der aktiven Geräte und der Geräte der letzten
 * 30 Tage, meiste aktive zuerst. Ohne Admins und gelöschte Konten.
 * Nur für Admins (Prüfung in der Datenbank).
 */
export async function getAdminSitzungenUebersicht(): Promise<{
  data: SitzungenUebersichtRow[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('admin_sitzungen_uebersicht');
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((row) => ({
        kontoId: asStr(row.konto_id),
        firma: asStrOrNull(row.firma),
        vorname: asStrOrNull(row.vorname),
        nachname: asStrOrNull(row.nachname),
        rolle: asRolle(row.rolle),
        nutzerNr: asStrOrNull(row.nutzer_nr),
        aktiveGeraete: asNum(row.aktive_geraete),
        geraete30Tage: asNum(row.geraete_30_tage),
        zuletztAktiv: asStrOrNull(row.zuletzt_aktiv),
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// MITARBEITER-ZUGÄNGE (DRPX-Pro, Migration 132, 15.10.2026)
// ════════════════════════════════════════════════════════════════════

const MITGLIED_STATUS_KEYS: readonly MitgliedStatus[] = ['eingeladen', 'aktiv', 'gesperrt'];

/** Validiert einen unbekannten DB-Wert gegen die `MitgliedStatus`-Union. */
function asMitgliedStatus(v: unknown): MitgliedStatus {
  const s = String(v ?? '');
  // Unbekanntes als 'gesperrt' — im Zweifel zu, nicht auf
  return (MITGLIED_STATUS_KEYS as readonly string[]).includes(s)
    ? (s as MitgliedStatus)
    : 'gesperrt';
}

/**
 * Lädt alle Zuordnungen Mitarbeiter-Zugang → Firma. Ein Admin sieht per
 * RLS alle Zeilen (Policy `firma_mitglieder_select`). Fehlt die Tabelle noch
 * (Migration 132 nicht ausgeführt), kommt eine leere Liste mit `error`.
 */
export async function getFirmaMitglieder(): Promise<{
  data: FirmaMitgliedRow[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('firma_mitglieder').select('user_id, firma_id, status');
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return {
      data: rows.map((row) => ({
        userId: asStr(row.user_id),
        firmaId: asStr(row.firma_id),
        status: asMitgliedStatus(row.status),
      })),
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt alle Blacklist-Einträge.
 */
export async function getSignupBlacklist(): Promise<{
  data: Array<Record<string, unknown>>;
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('signup_blacklist')
      .select('*')
      .order('created_at', { ascending: false });
    if (r.error) return { data: [], error: r.error };
    return { data: (r.data || []) as Array<Record<string, unknown>> };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Fügt einen Blacklist-Eintrag hinzu.
 */
export async function addBlacklistEntry(
  patternType: 'email_domain' | 'email_pattern' | 'phone_prefix',
  pattern: string,
  reason?: string,
): Promise<{ data?: Record<string, unknown>; error?: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const user = await client.auth.getUser();
    const r = await client
      .from('signup_blacklist')
      .insert({
        pattern_type: patternType,
        pattern: pattern,
        reason: reason || null,
        added_by: user.data.user ? user.data.user.id : null,
      })
      .select()
      .single();
    if (r.error) return { error: r.error };
    return { data: r.data as Record<string, unknown> };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Löscht einen Blacklist-Eintrag.
 */
export async function removeBlacklistEntry(
  id: number,
): Promise<{ success?: boolean; error?: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('signup_blacklist').delete().eq('id', id);
    if (r.error) return { error: r.error };
    return { success: true };
  } catch (e: unknown) {
    return { error: e };
  }
}
